import moment from 'moment';

import i18n from 'i18next';
import { Receipt } from 'models/receipt';
import { getFormattedPrice } from 'utils/currency';
import { fittingString } from 'utils/generic';

interface Theme {
  background: string;
  divison: string;
  label: string;
  value: string;
  discounts: string;
}

export const generateReceipt = async (receipt: Receipt, theme: Theme, locale: string) => {
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency: receipt.currencyCode, l: locale });
    return formatted;
  };

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) throw new Error('Could not get canvas context');

  canvas.width = 375;
  canvas.height = 812;
  context.fillStyle = theme.background;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const drawLabel = (
    text: string,
    x: number,
    y: number,
    options?: {
      size?: number;
    },
  ) => {
    context.font = `600 ${options?.size || 16}px Poppins`;
    context.fillStyle = theme.label;
    context.textAlign = 'left';
    context.fillText(text, x, y);
  };

  const drawValue = (
    text: string,
    x: number,
    y: number,
    options?: {
      color?: string;
      size?: number;
      weight?: number | string;
    },
  ) => {
    context.font = `${options?.weight || 'normal'} ${options?.size || 16}px Poppins`;
    context.fillStyle = options?.color || theme.value;
    context.textAlign = 'right';

    context.fillText(fittingString(context, text, canvas.width / 2), x, y);
  };

  const drawLine = (x: number) => {
    context.strokeStyle = theme.divison;
    context.moveTo(16, x + 30);
    context.lineTo(canvas.width - 16, x + 30);
    context.stroke();
  };

  const drawRappiLogo = () =>
    new Promise<void>((resolve) => {
      const image = new Image();
      image.onload = () => {
        context.drawImage(image, canvas.width / 2 - image.width / 2, 23);
        resolve();
      };
      image.src = '/images/logo-rappi-oficial.svg';
    });

  const drawType = () => {
    const position = { x: 16, y: 120 };
    drawLabel(i18n.t(`receipt.types.${receipt.type.toLowerCase()}`), position.x, position.y);
    drawValue(receipt.name, canvas.width - position.x, position.y);
    drawLine(position.y);
  };

  const drawDate = () => {
    const position = { x: 16, y: 190 };
    const date = moment(receipt.date);
    drawLabel(i18n.t('receipt.date'), position.x, position.y);
    drawValue(date.format(`DD/MM/YYYY [${i18n.t('receipt.at')}] hh:mma`), canvas.width - position.x, position.y);
    drawLine(position.y);
  };

  const drawTotal = () => {
    const position = { x: 16, y: 330 };
    const isHaveOptional = receipt.delivery || receipt.extra || receipt.discounts;

    if (isHaveOptional) {
      drawLabel(i18n.t('receipt.subtotal'), position.x, position.y);
      drawValue(formattedPrice(receipt.subtotal), canvas.width - position.x, position.y);
      position.y += 50;
    }

    if (receipt.delivery) {
      drawLabel(i18n.t('receipt.delivery'), position.x, position.y);
      drawValue(formattedPrice(receipt.delivery), canvas.width - position.x, position.y);
    }

    if (receipt.extra) {
      position.y += 25;
      drawLabel(i18n.t('receipt.extra'), position.x, position.y);
      drawValue(formattedPrice(receipt.extra), canvas.width - position.x, position.y);
    }

    if (receipt.discounts) {
      position.y += 25;
      drawLabel(i18n.t('receipt.discounts'), position.x, position.y);
      drawValue(`-${formattedPrice(receipt.discounts)}`, canvas.width - position.x, position.y, {
        color: theme.discounts,
      });
    }

    if (isHaveOptional) {
      position.y += 70;
    }

    drawLabel(i18n.t('receipt.total'), position.x, position.y, {
      size: 24,
    });
    drawValue(formattedPrice(receipt.total), canvas.width - position.x, position.y, {
      color: theme.label,
      size: 24,
      weight: 600,
    });
  };

  const createImage = () => canvas.toDataURL('image/png');

  await drawRappiLogo();
  await drawType();
  await drawDate();
  //await drawPayment();
  await drawTotal();

  return createImage();
};
